import { styled, Card, Box, TableCell, TableRow, Typography, alpha, keyframes } from '@mui/material';

// Flash animation for price changes - no scaling, just brightness change
const flash = keyframes`
  0% {
    opacity: 1;
    filter: brightness(1);
  }
  50% {
    opacity: 0.95;
    filter: brightness(1.2);
  }
  100% {
    opacity: 1;
    filter: brightness(1);
  }
`;

// Helper function to determine price card background color
const getPriceCardBackgroundColor = (theme, isPositive, noChange) => {
  const isDarkTheme = theme.palette.mode === 'dark';

  // If noChange is true, return a neutral gray color
  if (noChange) {
    return isDarkTheme
      ? alpha(theme.palette.grey[700], 0.25) // Dark neutral for dark mode
      : alpha(theme.palette.grey[300], 0.35); // Light neutral for light mode
  }  // For true values - green colors for upward price movement
  if (isPositive === true) {
    return isDarkTheme
      ? alpha(theme.palette.success.main, 0.12) // Extremely light green for dark mode
      : alpha(theme.palette.success.light, 0.12); // Extremely light green for light mode
  }

  // For false values - red colors for downward price movement
  if (isPositive === false) {
    return isDarkTheme
      ? alpha(theme.palette.error.main, 0.12) // Extremely light red for dark mode
      : alpha(theme.palette.error.light, 0.12); // Extremely light red for light mode
  }

  // Default case - use a neutral color with good visibility (same as noChange case)
  return isDarkTheme
    ? alpha(theme.palette.grey[700], 0.25) // Dark neutral for dark mode
    : alpha(theme.palette.grey[300], 0.35); // Light neutral for light mode
};

// Helper function to get change box background color
const getChangeBoxBgColor = (theme, isPositive) => {
  const isDarkTheme = theme.palette.mode === 'dark';

  if (isPositive) {
    return isDarkTheme
      ? alpha(theme.palette.success.main, 0.2)
      : alpha(theme.palette.success.main, 0.15);
  }

  return isDarkTheme
    ? alpha(theme.palette.error.main, 0.2)
    : alpha(theme.palette.error.main, 0.15);
};

// Helper function to get change box text color
const getChangeBoxTextColor = (theme, isPositive) => {
  const isDarkTheme = theme.palette.mode === 'dark';

  if (isPositive) {
    return isDarkTheme ? '#42d679' : '#00701a'; // Green color
  }

  return isDarkTheme ? '#ff5c5c' : '#b71c1c'; // Red color
};

// Helper function for price card box shadow
const getPriceCardBoxShadow = (theme, isPositive, noChange) => {
  // If no change, use a neutral shadow
  if (noChange) {
    return `0 0 0 1px ${alpha(theme.palette.divider, 0.2)}`;
  }  // For price movements (up or down)
  if (isPositive === true || isPositive === false) {
    const isDarkTheme = theme.palette.mode === 'dark';
    const opacity = isDarkTheme ? 0.08 : 0.08; // Extremely minimal opacity
    const color = isPositive === true ? theme.palette.success.main : theme.palette.error.main;

    // Return a simpler shadow for better performance
    return `0 0 0 1px ${alpha(color, opacity)}`;
  }

  // Return a subtle neutral shadow for null values
  return `0 0 0 1px ${alpha(theme.palette.divider, 0.2)}`;
};

// Helper function to determine price text color
const getPriceTextColor = (theme, isPositive) => {
  const isDarkTheme = theme.palette.mode === 'dark';

  // For no change, use default text color
  if (isPositive === null) {
    return theme.palette.text.primary;
  }

  // For price increase
  if (isPositive === true) {
    return isDarkTheme
      ? theme.palette.success.light // Brighter green for dark theme
      : '#00701a'; // Darker, more visible green for light theme
  }

  // For price decrease
  if (isPositive === false) {
    return isDarkTheme
      ? theme.palette.error.light // Brighter red for dark theme
      : '#b71c1c'; // Darker, more visible red for light theme
  }

  // Default color - more visible in both themes
  return theme.palette.text.primary;
};

// Helper function for price text shadow
const getPriceTextShadow = () => 'none'; // Removed text shadow for performance

export const StyledCard = styled(Card)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: 0,
  border: 'none',
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.standard,
  }),
  overflow: 'visible',
  position: 'sticky',
  top: 0,
  zIndex: 1050,
}));

export const ScriptCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.2, 0.6),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.04)}`,
  cursor: 'pointer',
  backgroundColor: theme.palette.background.paper,
  transition: theme.transitions.create(['background-color'], {
    duration: theme.transitions.duration.shorter,
  }),
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.01),
  },
  '& .MuiTypography-subtitle2': {
    color: theme.palette.text.primary,
  },
  height: '44px',
  display: 'flex',
  alignItems: 'center',
  marginBottom: 0,
  '&:last-child': {
    borderBottom: 'none',
    marginBottom: 0,
  },
}));

export const HeaderBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0.75, 2),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  backgroundColor: theme.palette.mode === 'light'
    ? theme.palette.background.neutral
    : theme.palette.grey[800],
  height: 36,
  display: 'flex',
  alignItems: 'center',
  position: 'sticky',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1050,
  width: '100%',
  borderRadius: 0,
  margin: 0,
  '& .MuiTypography-subtitle2': {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
  },
}));

export const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  padding: theme.spacing(0.75, 2),
  backgroundColor: theme.palette.mode === 'light'
    ? theme.palette.background.neutral
    : alpha(theme.palette.background.neutral, 0.2),
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: theme.palette.text.secondary,
  height: 36,
  position: 'sticky',
  top: 0,
  zIndex: 1050,
  borderRadius: 0
}));

export const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    cursor: 'pointer',
  },
  '& td': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    padding: theme.spacing(1.5, 2), // Increased padding for more height
    height: 60, // Increased height
  },
  marginBottom: theme.spacing(1.5), // This doesn't work directly on TableRow, will handle in rendering
  '&:last-child td': {
    borderBottom: 'none',
  },
}));

export const PriceBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(0.3),
  borderRadius: theme.shape.borderRadius * 0.75,
  height: '100%',
  width: '100%',
  '& .price-label': {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  }, '& .MuiTypography-subtitle2': {
    fontSize: '14px',
    fontWeight: 600,
  },
  '& .MuiTypography-caption': {
    fontSize: '10px !important',
    opacity: 0.8,
  }
}));

export const ChangeBox = styled(Box)(({ theme, isPositive }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: theme.spacing(0.1, 0.4),
  borderRadius: 3,
  backgroundColor: getChangeBoxBgColor(theme, isPositive),
  color: getChangeBoxTextColor(theme, isPositive),
  fontSize: 9,
  fontWeight: 700, // Increased from 600 for better visibility
  lineHeight: 1,
  gap: 2,
  height: '16px',
}));

export const PriceCard = styled(Box)(({ theme, isPositive, noChange, isFlashing }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(0.2, 0.3),
  borderRadius: 4,
  backgroundColor: getPriceCardBackgroundColor(theme, isPositive, noChange),
  height: '100%',
  width: '100%',
  transition: theme.transitions.create(['background-color', 'box-shadow'], {
    duration: '0.1s', // Faster transition for immediate feedback
  }),
  animation: isFlashing ? `${flash} 0.4s ease-out` : 'none', // Reduced from 0.6s to 0.4s
  minHeight: '34px',
  boxShadow: getPriceCardBoxShadow(theme, isPositive, noChange),
  // Text colors should reflect price movement while maintaining readability
  '& .MuiTypography-root.price-value': {
    color: getPriceTextColor(theme, isPositive),
    fontWeight: 700,
    fontSize: '15px',
    letterSpacing: '-0.3px',
    textShadow: getPriceTextShadow(),
    transition: 'color 0.1s ease',
  },
  // Secondary text remains the same for better readability
  '& .MuiTypography-caption': {
    color: theme.palette.text.secondary,
    fontSize: '10px !important',
  }
}));

export const PriceTypography = styled(Typography)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontWeight: 700, // Increased from 600 for better visibility
  fontSize: 14, // Increased from 13 for better visibility
  letterSpacing: -0.3,
  display: 'inline-flex',
  alignItems: 'baseline',
  '& .decimal': {
    fontSize: '85%',
    opacity: 0.9, // Increased from older value for better visibility
    marginLeft: 1,
  },
}));

export const formatPrice = (price) => {
  const [whole, decimal] = price.toFixed(2).split('.');
  return (
    <>
      {whole}
      <span className="decimal">.{decimal}</span>
    </>
  );
};
